// ── IngredientVarianceTab.js — 식재료 사용량 분석 ──
// 메뉴 판매 × 레시피 = 이론 사용량 vs 영수증 = 실제 매입 비교
// 편차로 낭비·도난·레시피 오차·재고 누락 신호 감지

import React, { useState, useMemo } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../db';
import { analyzeVariance } from '../utils/ingredientVariance';
import { useIsMobile } from '../useIsMobile';
import { useTransactions } from '../useTransactions';

const fmt = n => Math.round(Number(n) || 0).toLocaleString('ko-KR');
const fmtQty = n => {
  const x = Number(n) || 0;
  if (Math.abs(x) < 10) return x.toFixed(2);
  return Math.round(x).toLocaleString('ko-KR');
};
const fmtSigned = n => (n > 0 ? '+' : '') + fmt(n);
const fmtSignedQty = n => (n > 0 ? '+' : '') + fmtQty(n);

function getCurrentMonth() {
  const d = new Date();
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
}

export default function IngredientVarianceTab({
  user,
  categories = [],
  prices = [],
  inventory = {},
  menuRecipeMap = {},
  onMenuRecipeMapChange,
}) {
  const isMobile = useIsMobile();
  const [month, setMonth] = useState(getCurrentMonth);
  // 12개월 lazy 쿼리
  const dateRange = useMemo(() => {
    const start = new Date(month + '-01');
    start.setMonth(start.getMonth() - 11);
    const startDate = start.getFullYear() + '-' + String(start.getMonth() + 1).padStart(2, '0') + '-01';
    const endDate = month + '-31';
    return { startDate, endDate };
  }, [month]);
  const { transactions } = useTransactions(user, {
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    limitCount: 5000,
  });
  const onMonthChange = setMonth;
  const [purchaseLog, setPurchaseLog] = useState([]);
  const [loadingLog, setLoadingLog] = useState(false);
  const [inventoryDelta, setInventoryDelta] = useState({});  // { name: number }
  const [showAll, setShowAll] = useState(false);  // 적정 항목까지 다 보이기
  const [editingDelta, setEditingDelta] = useState(false);

  // purchaseLog Firestore에서 직접 fetch (한 번만)
  React.useEffect(() => {
    if (!user?.uid) return;
    let cancelled = false;
    setLoadingLog(true);
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'users', user.uid, 'purchaseLog'));
        if (cancelled) return;
        const logs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setPurchaseLog(logs);
      } catch (e) { console.warn('purchaseLog fetch failed:', e.message); }
      finally { if (!cancelled) setLoadingLog(false); }
    })();
    return () => { cancelled = true; };
  }, [user?.uid]);

  const analysis = useMemo(() => analyzeVariance({
    transactions,
    purchaseLog,
    categories,
    prices,
    inventory,
    monthKey: month,
    inventoryDelta,
    menuRecipeMap,
  }), [transactions, purchaseLog, categories, prices, inventory, month, inventoryDelta, menuRecipeMap]);

  // 매핑 변경 핸들러
  const handleMappingChange = (saleName, recipeIdOrSpecial) => {
    if (!onMenuRecipeMapChange) return;
    const next = { ...(menuRecipeMap || {}) };
    if (!recipeIdOrSpecial) {
      // auto로 되돌리기 (수동 매핑 제거)
      delete next[saleName];
    } else {
      next[saleName] = recipeIdOrSpecial;
    }
    onMenuRecipeMapChange(next);
  };

  // 모든 레시피 모으기 (드롭다운 옵션용)
  const allRecipes = [];
  for (const cat of (categories || [])) {
    for (const item of (cat.items || [])) {
      if (item.type === 'menu') {
        allRecipes.push({ id: item.id, name: item.name, category: cat.name });
      }
    }
  }

  const visibleRows = showAll
    ? analysis.rows
    : analysis.rows.filter(r => r.status.tag !== 'ok' && r.status.tag !== 'none');

  const updateDelta = (name, value) => {
    setInventoryDelta(prev => ({ ...prev, [name]: Number(value) || 0 }));
  };

  return (
    <div style={{ padding: isMobile ? '14px' : '20px', maxWidth: 1200, margin: '0 auto' }}>
      {/* 월 선택 */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 12, padding: '10px 14px', background: '#fff', borderRadius: 10,
        marginBottom: 14, boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#6b7280' }}>📅 기간</span>
          <input type="month" value={month}
                 onChange={e => onMonthChange && onMonthChange(e.target.value)}
                 style={{
                   padding: '6px 10px', border: '1px solid #d1d5db',
                   borderRadius: 6, fontSize: 13, fontWeight: 600, color: '#18181B',
                 }} />
        </div>
        <button onClick={() => setShowAll(s => !s)}
                style={{
                  padding: '6px 12px', borderRadius: 6,
                  border: '1px solid #d1d5db', background: '#fff',
                  fontSize: 12, color: '#374151', cursor: 'pointer',
                }}>
          {showAll ? '편차만 보기' : '전체 보기'}
        </button>
      </div>

      {/* 안내 */}
      <div style={{
        background: '#fefce8', border: '1px solid #fde047', borderRadius: 8,
        padding: '10px 14px', marginBottom: 14, fontSize: 12, color: '#713f12', lineHeight: 1.6,
      }}>
        💡 <strong>적정 매입원가</strong> = 이론 사용량 원가 + 안전재고 보충분 원가.<br/>
        실제 매입이 적정 매입보다 <strong>크게 부족</strong>하면 재고 바닥 위험, <strong>크게 과다</strong>면 낭비/현금 묶임 신호.<br/>
        <strong>주의</strong>: 메뉴별 판매수량이 입력돼 있어야 정확. POS 일괄 등록만 사용 중이면 메뉴 매칭이 안 됩니다.
      </div>

      {/* 요약 카드 */}
      <div style={{
        display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)',
        gap: 10, marginBottom: 14,
      }}>
        <SummaryCard label="이론 식재료 원가" value={fmt(analysis.totals.theoreticalCost) + '원'} color="#FF6F0F" />
        <SummaryCard label="실제 매입 금액" value={fmt(analysis.totals.actualCost) + '원'} color="#0284c7" />
        <SummaryCard
          label="적정 매입원가"
          value={fmt(analysis.totals.adequatePurchaseCost || 0) + '원'}
          color="#9333ea"
          sub={`보충 필요 ${analysis.totals.replenishNeededCount || 0}개 재료`}
        />
        <SummaryCard
          label="편차 (실매입 - 이론)"
          value={fmtSigned(analysis.totals.varianceCost) + '원'}
          color={analysis.totals.varianceCost > 0 ? '#52525B' : '#FF6F0F'}
        />
        <SummaryCard
          label="매칭된 메뉴"
          value={`${analysis.totals.matchedMenuCount}/${analysis.totals.totalMenuCount}`}
          color="#7c3aed"
          sub="레시피 등록된 메뉴 수"
        />
      </div>

      {/* 메뉴 매칭 경고 */}
      {analysis.totals.totalMenuCount > 0 && analysis.totals.matchedMenuCount === 0 && (
        <div style={{
          background: '#fef2f2', border: '1px solid #E4E4E7', borderRadius: 8,
          padding: '12px 14px', marginBottom: 14, fontSize: 13, color: '#52525B', lineHeight: 1.6,
        }}>
          ⚠️ <strong>이번 달 판매된 메뉴 중 레시피에 등록된 것이 없습니다.</strong> 레시피 탭에서 메뉴를 등록해야 이론 사용량 계산이 가능합니다.
        </div>
      )}
      {analysis.totals.totalMenuCount === 0 && (
        <div style={{
          background: '#FAFAFA', border: '1px solid #fde68a', borderRadius: 8,
          padding: '12px 14px', marginBottom: 14, fontSize: 13, color: '#52525B', lineHeight: 1.6,
        }}>
          ℹ️ 이번 달 <strong>메뉴별 판매 데이터</strong>가 없습니다. 매출 입력 탭 → "메뉴별 입력 모드"로 입력하면 비교 가능.
        </div>
      )}

      {/* 메뉴 ↔ 레시피 매칭 디버그 */}
      <MenuMappingCard
        mapping={analysis.menuMapping || []}
        allRecipes={allRecipes}
        menuRecipeMap={menuRecipeMap}
        onMappingChange={handleMappingChange}
      />

      {/* 재고 변화 입력 토글 */}
      <div style={{ marginBottom: 14 }}>
        <button onClick={() => setEditingDelta(d => !d)}
                style={{
                  padding: '8px 14px', borderRadius: 6,
                  border: '1px solid #d1d5db', background: '#fff',
                  fontSize: 13, color: '#374151', cursor: 'pointer', fontWeight: 600,
                }}>
          📦 재고 변화 입력 {editingDelta ? '닫기' : '열기'}
        </button>
        {editingDelta && (
          <div style={{
            marginTop: 8, padding: 12, background: '#f9fafb', borderRadius: 8,
            fontSize: 12, color: '#374151', lineHeight: 1.6,
          }}>
            아래 표에서 <strong>재고 Δ</strong> 칸을 채우세요. <strong>기말 재고 - 기초 재고</strong> 값.<br/>
            예) 월초에 당근 5kg, 월말에 1kg → -4 입력 (4kg 줄어듦 = 추가 사용)<br/>
            기본값 0 = 재고 변동 없음 (steady state 가정)
          </div>
        )}
      </div>

      {/* 식재료 비교 테이블/카드 */}
      {visibleRows.length === 0 ? (
        <div style={{
          padding: 40, textAlign: 'center', background: '#fff', borderRadius: 12,
          fontSize: 14, color: '#9ca3af',
        }}>
          {analysis.rows.length === 0
            ? '비교할 데이터가 없습니다 (메뉴 판매 + 영수증 매입 둘 다 필요)'
            : '편차가 큰 항목 없음 — 모두 적정 범위 ✅'}
          {!showAll && analysis.rows.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <button onClick={() => setShowAll(true)}
                      style={{ padding: '8px 16px', borderRadius: 6,
                               background: '#FF6F0F', color: '#fff', border: 'none',
                               cursor: 'pointer', fontSize: 13 }}>
                전체 {analysis.rows.length}개 보기
              </button>
            </div>
          )}
        </div>
      ) : isMobile ? (
        <MobileCardList rows={visibleRows} editingDelta={editingDelta} onDelta={updateDelta} />
      ) : (
        <DesktopTable rows={visibleRows} editingDelta={editingDelta} onDelta={updateDelta} />
      )}

      {loadingLog && (
        <div style={{ textAlign: 'center', padding: 12, fontSize: 12, color: '#9ca3af' }}>
          매입메모 불러오는 중...
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, color, sub }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 10, padding: '12px 14px',
      borderLeft: `4px solid ${color}`, boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
    }}>
      <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 17, fontWeight: 800, color }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function DesktopTable({ rows, editingDelta, onDelta }) {
  return (
    <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: '#f9fafb', textAlign: 'left' }}>
            <th style={cellStyle('left', true)}>재료명</th>
            <th style={cellStyle('right', true)}>이론 사용량</th>
            <th style={cellStyle('right', true)}>실제 매입</th>
            <th style={cellStyle('right', true)}>현재 재고</th>
            <th style={cellStyle('right', true)}>안전재고</th>
            <th style={cellStyle('right', true)}>적정 매입</th>
            <th style={cellStyle('right', true)}>편차 (원)</th>
            <th style={cellStyle('center', true)}>매입 평가</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.name + i} style={{ borderTop: '1px solid #f3f4f6' }}>
              <td style={cellStyle('left')}>
                <strong>{r.name}</strong>
                {!r.matched && <span style={{ fontSize: 10, color: '#52525B', marginLeft: 6 }}>단가표 미매칭</span>}
              </td>
              <td style={cellStyle('right')}>
                {fmtQty(r.theoreticalQty)} {r.unit}<br/>
                <span style={{ fontSize: 11, color: '#6b7280' }}>{fmt(r.theoreticalCost)}원</span>
              </td>
              <td style={cellStyle('right')}>
                {fmtQty(r.actualQty)} {r.unit}<br/>
                <span style={{ fontSize: 11, color: '#6b7280' }}>{fmt(r.actualCost)}원</span>
              </td>
              <td style={cellStyle('right')}>
                {r.currentStock !== null ? fmtQty(r.currentStock) + ' ' + r.unit : '—'}
              </td>
              <td style={cellStyle('right')}>
                {r.safetyStock > 0 ? fmtQty(r.safetyStock) + ' ' + r.unit : '—'}
                {r.stockGap > 0 && (
                  <div style={{ fontSize: 10, color: '#52525B', fontWeight: 600 }}>
                    보충 {fmtQty(r.stockGap)} {r.unit}
                  </div>
                )}
              </td>
              <td style={cellStyle('right')}>
                {fmtQty(r.adequatePurchaseQty)} {r.unit}<br/>
                <span style={{ fontSize: 11, color: '#6b7280' }}>{fmt(r.adequatePurchaseCost)}원</span>
              </td>
              <td style={{ ...cellStyle('right'), color: r.purchaseVarianceCost > 0 ? '#52525B' : '#FF6F0F', fontWeight: 600 }}>
                {fmtSigned(r.purchaseVarianceCost)}원
              </td>
              <td style={{ ...cellStyle('center'), color: r.purchaseStatus.color, fontWeight: 600, fontSize: 12 }}>
                {r.purchaseStatus.label}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MobileCardList({ rows, editingDelta, onDelta }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {rows.map((r, i) => (
        <div key={r.name + i} style={{
          background: '#fff', borderRadius: 10, padding: 12,
          boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
          borderLeft: `4px solid ${r.purchaseStatus.color}`,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#1f2937' }}>{r.name}</div>
            <div style={{ fontSize: 11, fontWeight: 600, color: r.purchaseStatus.color }}>{r.purchaseStatus.label}</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, fontSize: 12, color: '#374151' }}>
            <div>이론원가: <strong>{fmt(r.theoreticalCost)}원</strong></div>
            <div>실제 매입: <strong>{fmt(r.actualCost)}원</strong></div>
            <div>적정 매입: <strong>{fmt(r.adequatePurchaseCost)}원</strong></div>
            <div>매입 편차: <strong style={{ color: r.purchaseVarianceCost > 0 ? '#52525B' : '#FF6F0F' }}>{fmtSigned(r.purchaseVarianceCost)}원</strong></div>
            {r.stockGap > 0 && (
              <div style={{ gridColumn: '1 / -1', fontSize: 11, color: '#52525B' }}>
                ⚠️ 안전재고 보충 필요: {fmtQty(r.stockGap)} {r.unit}
              </div>
            )}
            {editingDelta && (
              <div style={{ gridColumn: '1 / -1', paddingTop: 4 }}>
                재고 Δ:&nbsp;
                <input type="number" step="0.1"
                       value={r.delta || ''}
                       onChange={e => onDelta(r.name, e.target.value)}
                       placeholder="0"
                       style={{ width: 70, padding: '4px 6px', border: '1px solid #d1d5db',
                                borderRadius: 4, fontSize: 12, textAlign: 'right' }} />
                <span style={{ fontSize: 10, color: '#9ca3af', marginLeft: 6 }}>(기말 - 기초)</span>
              </div>
            )}
            {r.currentStock !== null && (
              <div style={{ gridColumn: '1 / -1', fontSize: 11, color: '#9ca3af' }}>
                현재 재고: {fmtQty(r.currentStock)} {r.unit}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function cellStyle(align, header) {
  return {
    padding: '10px 12px',
    textAlign: align,
    fontWeight: header ? 700 : 400,
    fontSize: header ? 12 : 13,
    color: header ? '#374151' : '#1f2937',
    whiteSpace: 'nowrap',
  };
}


// ── 메뉴 → 레시피 매칭 디버그 + 수동 매핑 카드 ──
function MenuMappingCard({ mapping, allRecipes = [], menuRecipeMap = {}, onMappingChange }) {
  const [open, setOpen] = useState(false);
  if (!mapping || mapping.length === 0) return null;

  const matched = mapping.filter(m => m.matchedRecipe);
  const unmatched = mapping.filter(m => !m.matchedRecipe);

  // 같은 레시피로 매핑된 메뉴 그룹화 — 여러 메뉴가 한 레시피로 묶이면 문제 신호
  const recipeGroups = {};
  for (const m of matched) {
    const key = m.matchedRecipe;
    if (!recipeGroups[key]) recipeGroups[key] = [];
    recipeGroups[key].push(m);
  }
  const multiMappedRecipes = Object.entries(recipeGroups).filter(([_, items]) => items.length > 1);

  return (
    <div style={{
      background: '#fff', borderRadius: 10, marginBottom: 14,
      boxShadow: '0 1px 4px rgba(0,0,0,0.04)', overflow: 'hidden',
    }}>
      <button onClick={() => setOpen(!open)} style={{
        width: '100%', padding: '12px 14px',
        background: 'transparent', border: 'none', cursor: 'pointer',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        fontSize: 13, fontWeight: 700, color: '#374151',
      }}>
        <span>
          🔍 메뉴 매칭 결과 보기 ({matched.length}개 매칭 / {unmatched.length}개 미매칭)
          {multiMappedRecipes.length > 0 && (
            <span style={{ color: '#52525B', marginLeft: 8, fontSize: 11 }}>
              ⚠️ {multiMappedRecipes.length}개 레시피가 여러 메뉴로 매칭됨
            </span>
          )}
        </span>
        <span style={{ fontSize: 11, color: '#6b7280' }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{ padding: '0 14px 14px', fontSize: 12, color: '#374151' }}>
          {multiMappedRecipes.length > 0 && (
            <div style={{
              background: '#fef2f2', border: '1px solid #E4E4E7',
              borderRadius: 6, padding: 10, marginBottom: 10, lineHeight: 1.6,
            }}>
              <strong style={{ color: '#52525B' }}>⚠️ fuzzy 매칭으로 한 레시피에 여러 메뉴가 묶임:</strong><br/>
              <span style={{ color: '#6b7280', fontSize: 11 }}>
                레시피 이름을 메뉴와 정확히 일치시키거나, 메뉴별로 개별 레시피 등록 필요
              </span>
              <div style={{ marginTop: 6 }}>
                {multiMappedRecipes.map(([recipe, items]) => (
                  <div key={recipe} style={{ marginBottom: 4 }}>
                    📋 <strong>{recipe}</strong> ← {items.map(i => `${i.saleName}(${i.saleQty})`).join(', ')}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ marginTop: 6 }}>
            <div style={{ fontWeight: 600, marginBottom: 6, color: '#1f2937' }}>
              ✅ 매칭된 메뉴 ({matched.length}개) <span style={{ fontSize: 11, fontWeight: 400, color: '#6b7280' }}>— 잘못 매칭됐으면 드롭다운에서 수정</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {matched.map((m, i) => (
                <MenuMappingRow
                  key={m.saleName + i}
                  m={m}
                  allRecipes={allRecipes}
                  currentValue={menuRecipeMap[m.saleName] || ''}
                  onChange={onMappingChange}
                  isMatched={true}
                />
              ))}
            </div>
          </div>

          {unmatched.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontWeight: 600, marginBottom: 6, color: '#1f2937' }}>
                ❌ 레시피 없음 ({unmatched.length}개) <span style={{ fontSize: 11, fontWeight: 400, color: '#6b7280' }}>— 드롭다운에서 매핑 가능</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {unmatched.map((m, i) => (
                  <MenuMappingRow
                    key={m.saleName + i}
                    m={m}
                    allRecipes={allRecipes}
                    currentValue={menuRecipeMap[m.saleName] || ''}
                    onChange={onMappingChange}
                    isMatched={false}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ── 메뉴 매핑 한 행 (드롭다운 포함) ──
function MenuMappingRow({ m, allRecipes, currentValue, onChange, isMatched }) {
  const bg = isMatched ? '#f9fafb' : '#F4F4F5';
  const sourceLabel = m.matchSource === 'manual' ? '🔧 수동' : m.matchSource === 'fuzzy' ? '🤖 자동' : '';
  return (
    <div style={{
      padding: '6px 10px', background: bg, borderRadius: 4,
      display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
    }}>
      <div style={{ flex: '1 1 200px', minWidth: 0 }}>
        <strong>{m.saleName}</strong>
        <span style={{ color: '#6b7280', marginLeft: 6, fontSize: 11 }}>({m.saleQty}개)</span>
        {m.matchedRecipe && (
          <span style={{ color: '#18181B', marginLeft: 6, fontSize: 12 }}>
            → 📋 {m.matchedRecipe} {sourceLabel && <span style={{ fontSize: 10, color: '#9ca3af' }}>{sourceLabel}</span>}
          </span>
        )}
      </div>
      <select
        value={currentValue}
        onChange={(e) => onChange && onChange(m.saleName, e.target.value)}
        style={{
          padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: 4,
          fontSize: 11, background: '#fff', cursor: 'pointer', maxWidth: 200,
        }}
      >
        <option value="">자동 매칭</option>
        <option value="__exclude__">✕ 제외 (분석에서 빼기)</option>
        <optgroup label="── 레시피 ──">
          {allRecipes.map(r => (
            <option key={r.id} value={r.id}>{r.category ? `[${r.category}] ` : ''}{r.name}</option>
          ))}
        </optgroup>
      </select>
    </div>
  );
}
